'use client';

import { useState } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { IconButton } from '../../../../../components/icon-button';
import { HttpEditor } from '../../../../http-editor';
import { renderHttpMethodText } from '../../../../../utils/http-badge.utils';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  FileScriptIcon,
  SecurityCheckIcon,
  ShieldBanIcon,
} from '@terra/ui-icons';
import type { ParsedHarEntry } from '../types';
import { useTranslations } from 'next-intl';
import {
  formatHarRequestForEditor,
  formatHarResponseForEditor,
  getHeaderContentType,
  parseHarResponseStatus,
} from './har-entries-renderer.utils';

export {
  formatHarRequestForEditor,
  formatHarResponseForEditor,
  getHeaderContentType,
};
type HarEntriesRendererProps = {
  entries: ParsedHarEntry[];
  query?: string;
  totalEntries?: number;
  rawContent?: string;
  maxHeight?: string;
};
type HarEntryRowProps = {
  entry: ParsedHarEntry;
  isExpanded: boolean;
  onToggle: () => void;
};

/**
 * HttpEditor automatically uses context for Send to Replay and Send to Comparer
 */
function HarEntryRow({ entry, isExpanded, onToggle }: HarEntryRowProps) {
  const t = useTranslations('designSystem.chat.harEntriesRenderer');
  const displayText = entry.url || entry.id || 'HAR Entry';
  const isPublicSession =
    entry.authInfo?.toLowerCase().includes('no auth') ||
    entry.authInfo?.toLowerCase().includes('public');
  const requestContent = formatHarRequestForEditor(entry);
  const responseStatusCode = parseHarResponseStatus(entry.responseStatus);
  const responseContent = formatHarResponseForEditor(entry);
  const hasRequest = !!entry.url;
  const hasResponseBody = !!entry.bodyPreview && entry.bodyPreview !== 'N/A';
  const hasResponse =
    responseStatusCode !== undefined &&
    (hasResponseBody || (entry.responseHeaders?.length ?? 0) > 0);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      width="100%"
    >
      {/* Summary Row */}
      <HStack
        p={1.5}
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'bg.subtle' }}
        width="100%"
      >
        <HStack gap={1.5} flex="1" minW={0}>
          <IconButton size="xs" variant="ghost" aria-label="Toggle details">
            {isExpanded ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </IconButton>

          {/* Method badge */}
          {entry.method && renderHttpMethodText(entry.method)}

          {/* Response status */}
          {responseStatusCode ? renderHttpMethodText(responseStatusCode) : null}

          {/* Auth indicator - icon only */}
          {entry.authInfo && (
            <Box
              color={isPublicSession ? 'fg.disabled' : 'fg.muted'}
              flexShrink={0}
            >
              {isPublicSession ? (
                <ShieldBanIcon size={12} />
              ) : (
                <SecurityCheckIcon size={12} />
              )}
            </Box>
          )}

          {/* URL */}
          <HStack gap={1} alignItems="center" flex={1} minWidth={0}>
            <Box color="fg.subtle" flexShrink={0}>
              <FileScriptIcon size={10} />
            </Box>
            <Text
              fontSize="2xs"
              fontFamily="mono"
              color="fg.default"
              truncate
              flex={1}
            >
              {displayText}
            </Text>
          </HStack>
        </HStack>
      </HStack>

      {/* Expanded Content - HTTP Request/Response */}
      {isExpanded && (
        <Box borderTopWidth="1px" borderColor="border.subtle" bg="bg.canvas">
          {/* HTTP Request/Response Editor - Side by Side */}
          {(hasRequest || hasResponse) && (
            <HStack
              alignItems="stretch"
              gap="0"
              width="100%"
              minH="250px"
              maxH="350px"
            >
              {/* Request Panel */}
              {hasRequest && (
                <Box flex="1" minH={0} minW={0}>
                  <HttpEditor
                    value={requestContent}
                    title="Request"
                    readOnly
                    showViewModeToggle
                    showStatusBar={false}
                  />
                </Box>
              )}

              {/* Divider */}
              {hasRequest && hasResponse && (
                <Box w="1px" bg="border.subtle" flexShrink={0} />
              )}

              {/* Response Panel */}
              {hasResponse && (
                <Box flex="1" minH={0} minW={0}>
                  <HttpEditor
                    value={responseContent}
                    title="Response"
                    statusCode={responseStatusCode}
                    readOnly
                    showViewModeToggle
                    showStatusBar={false}
                  />
                </Box>
              )}
            </HStack>
          )}

          {/* Match reason and ID */}
          {(entry.matchReason || entry.id) && (
            <Box p={2} borderTopWidth="1px" borderColor="border.subtle">
              <VStack gap={1} alignItems="stretch">
                {entry.matchReason && (
                  <Box>
                    <Text
                      fontSize="2xs"
                      fontWeight="medium"
                      color="fg.muted"
                      mb={0.5}
                    >
                      {t('matchReason')}
                    </Text>
                    <Text fontSize="xs" color="fg.default" lineHeight="1.4">
                      {entry.matchReason}
                    </Text>
                  </Box>
                )}
                {entry.id && (
                  <Text fontSize="2xs" color="fg.subtle" fontFamily="mono">
                    {t('id', { id: entry.id })}
                  </Text>
                )}
              </VStack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

const MAX_DISPLAYED_ENTRIES = 5;

export function HarEntriesRenderer({
  entries,
  query,
  totalEntries,
  maxHeight = '600px',
}: HarEntriesRendererProps) {
  const t = useTranslations('designSystem.chat.harEntriesRenderer');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const displayedEntries = entries.slice(0, MAX_DISPLAYED_ENTRIES);
  const hiddenCount = entries.length - displayedEntries.length;
  const hasSearchMetadata = query || totalEntries !== undefined;
  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <VStack
      gap={1}
      w="full"
      maxHeight={maxHeight}
      overflowY="auto"
      overflowX="hidden"
      alignItems="stretch"
    >
      {/* Search metadata header */}
      {hasSearchMetadata && (
        <HStack
          px={2}
          py={1}
          gap={2}
          flexWrap="wrap"
          borderBottomWidth="1px"
          borderColor="border.subtle"
        >
          {query && (
            <Text fontSize="2xs" color="fg.muted">
              {t('query', { query })}
            </Text>
          )}
          {totalEntries !== undefined && (
            <Text fontSize="2xs" color="fg.muted">
              {t('foundEntries', {
                displayed: entries.length,
                total: totalEntries,
              })}
            </Text>
          )}
        </HStack>
      )}

      {displayedEntries.length > 0 ? (
        displayedEntries.map((entry, index) => (
          <HarEntryRow
            key={entry.id || `har-entry-${index}`}
            entry={entry}
            isExpanded={expandedIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))
      ) : (
        <Box
          p={4}
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          textAlign="center"
        >
          <Box color="fg.subtle" mb={2}>
            <FileScriptIcon size={24} />
          </Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={1}>
            {t('emptyTitle')}
          </Text>
          {query && (
            <Text fontSize="2xs" color="fg.subtle">
              {t('emptyDescription')}
            </Text>
          )}
        </Box>
      )}
      {hiddenCount > 0 && (
        <Box px={1.5} py={0.5}>
          <Text fontSize="2xs" color="fg.subtle" fontStyle="italic">
            {t('moreEntries', { count: hiddenCount })}
          </Text>
        </Box>
      )}
    </VStack>
  );
}
